import { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { inputCls } from "@/lib/catalog";
import { SQ, problems } from "@/components/settings/LineTools";
import { Segmented } from "@/components/ui/segmented";

const cell = `${inputCls.replace("w-full", "")} w-28 text-right font-mono-num`;
const r4 = (v) => Math.round(v * 1e4) / 1e4;

// One glass band table + glass options (laminated etc.) applied to every window/door product line.
export function GlassBandsSection() {
  const [m2, setM2] = useState(true);
  const [d, setD] = useState(null);
  const [errs, setErrs] = useState([]);
  const k = m2 ? SQ : 1;
  useEffect(() => { api.get("/settings/glass-bands").then((r) => setD(r.data)).catch((e) => toast.error(errorMessage(e))); }, []);
  if (!d) return null;
  const setT = (i, c, v) => setD({ ...d, glass_tiers: d.glass_tiers.map((t, j) => (j === i ? { ...t, [c]: v === "" ? "" : c === "rate" ? Number(v) / k : Number(v) * k } : t)) });
  const setU = (i, c, v) => setD({ ...d, glass_upgrades: d.glass_upgrades.map((u, j) => (j === i ? { ...u, [c]: c === "rate" ? Number(v) / k : v } : u)) });
  const save = async () => {
    const bad = problems({ glass_tiers: d.glass_tiers, glass_upgrades: d.glass_upgrades });
    setErrs(bad);
    if (bad.length) return;
    try {
      const { data } = await api.put("/settings/glass-bands", d);
      toast.success(`Glass bands saved on ${data.lines} product lines`);
    } catch (e) { setErrs([errorMessage(e)]); }
  };
  const U = m2 ? "m²" : "sq ft";
  return (
    <section data-testid="glass-bands-section" className="rounded-2xl border border-slate-800 p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-bold text-slate-100 md:text-lg">Glass bands (per pane) & glass options</h2>
        <Segmented testId="glass-bands-unit-toggle" value={m2 ? "m2" : "sqft"} options={[["sqft", "sq ft"], ["m2", "m²"]]} onChange={(v) => setM2(v === "m2")} />
      </div>
      <p className="mt-1 text-sm text-slate-400">Each pane is charged by its own area band. A glass option replaces the band for that pane. Saving applies to every window and door line.</p>
      <div className="mt-3 space-y-1">
        <div className="flex gap-1 text-[10px] text-slate-500"><span className="w-28 text-right">Over ({U})</span><span className="w-28 text-right">Up to ({U})</span><span className="w-28 text-right">$/{U}</span></div>
        {d.glass_tiers.map((t, i) => (
          <div key={i} className="flex gap-1" data-testid={`glass-band-row-${i}`}>
            <input aria-label="Over" data-testid={`glass-band-from-${i}`} value={t.from_sqft === "" ? "" : r4(t.from_sqft / k)} onChange={(e) => setT(i, "from_sqft", e.target.value)} className={cell} />
            <input aria-label="Up to" data-testid={`glass-band-to-${i}`} value={t.to_sqft === "" ? "" : r4(t.to_sqft / k)} onChange={(e) => setT(i, "to_sqft", e.target.value)} className={cell} />
            <input aria-label="Rate" data-testid={`glass-band-rate-${i}`} value={r4(t.rate * k)} onChange={(e) => setT(i, "rate", e.target.value)} className={cell} />
            <button aria-label="Remove" data-testid={`glass-band-remove-${i}`} onClick={() => setD({ ...d, glass_tiers: d.glass_tiers.filter((_, j) => j !== i) })} className="px-1 text-slate-500 hover:text-red-300"><X className="h-4 w-4" /></button>
          </div>
        ))}
        <button data-testid="glass-band-add" onClick={() => setD({ ...d, glass_tiers: [...d.glass_tiers, { from_sqft: 0, to_sqft: 0, rate: 0 }] })} className="inline-flex items-center gap-1 text-xs text-amber-300"><Plus className="h-3 w-3" /> Add band</button>
      </div>
      <div className="mt-4 space-y-1">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Glass options</p>
        {d.glass_upgrades.map((u, i) => (
          <div key={i} className="flex gap-1">
            <input aria-label="Name" data-testid={`glass-option-name-${i}`} value={u.name} onChange={(e) => setU(i, "name", e.target.value)} className={`${inputCls.replace("w-full", "")} w-64`} />
            <input aria-label="Rate" data-testid={`glass-option-rate-${i}`} value={r4(u.rate * k)} onChange={(e) => setU(i, "rate", e.target.value)} className={cell} />
            <button aria-label="Remove" onClick={() => setD({ ...d, glass_upgrades: d.glass_upgrades.filter((_, j) => j !== i) })} className="px-1 text-slate-500 hover:text-red-300"><X className="h-4 w-4" /></button>
          </div>
        ))}
        <button data-testid="glass-option-add" onClick={() => setD({ ...d, glass_upgrades: [...d.glass_upgrades, { name: "", rate: 0 }] })} className="inline-flex items-center gap-1 text-xs text-amber-300"><Plus className="h-3 w-3" /> Add option</button>
      </div>
      {errs.length > 0 && <ul data-testid="glass-bands-errors" className="mt-3 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">{errs.map((e) => <li key={e}>{e}</li>)}</ul>}
      <button data-testid="glass-bands-save" onClick={save} className="mt-3 rounded-full bg-amber-500 px-4 py-1.5 text-sm font-semibold text-slate-950 hover:bg-amber-400">Save glass bands</button>
    </section>
  );
}
